<script setup lang="ts">
import { computed } from 'vue';
import BaseCard from '@/components/ui/BaseCard.vue';
import type { CategoryCount } from '@/api/dashboard-types';

const props = defineProps<{
  categories: CategoryCount[];
  loading?: boolean;
}>();

const sorted = computed(() =>
  [...props.categories].sort((a, b) => b.count - a.count),
);

const maxCount = computed(() =>
  sorted.value.length > 0 ? sorted.value[0]!.count : 1,
);

function widthPct(count: number): string {
  if (maxCount.value === 0) return '0%';
  return `${Math.round((count / maxCount.value) * 100)}%`;
}
</script>

<template>
  <BaseCard padding="md" data-testid="category-breakdown">
    <template #header>
      <h2 class="text-section text-fg">By category</h2>
    </template>

    <div v-if="loading" class="flex flex-col gap-3">
      <div v-for="i in 4" :key="i" class="h-5 bg-canvas-soft rounded animate-pulse" />
    </div>

    <ul v-else-if="sorted.length > 0" class="flex flex-col gap-2">
      <li
        v-for="item in sorted"
        :key="item.category"
        class="flex items-center gap-3"
      >
        <span class="w-24 text-caption text-muted truncate flex-shrink-0">
          {{ item.category }}
        </span>
        <div class="flex-1 min-w-0 flex items-center gap-2">
          <div class="flex-1 bg-canvas-soft rounded-pill h-2 overflow-hidden">
            <div
              class="h-full bg-navy-600 rounded-pill transition-all duration-500"
              :style="{ width: widthPct(item.count) }"
            />
          </div>
          <span class="font-mono tabular-nums text-caption text-fg w-8 text-right flex-shrink-0">
            {{ item.count }}
          </span>
        </div>
      </li>
    </ul>

    <p v-else class="text-body text-muted">No category data.</p>
  </BaseCard>
</template>
